/**
 * VoxelGrid - Core 3D grid data structure
 * 
 * Provides efficient storage and access patterns for voxel data.
 */

import { Box3, Vector3 } from 'three';
import type { VoxelGridConfig, VoxelGridInfo } from './types';

export class VoxelGrid {
  public readonly info: VoxelGridInfo;
  public readonly data: Float32Array;

  constructor(info: VoxelGridInfo, initialValue = 0) {
    this.info = info;
    this.data = new Float32Array(info.total);
    if (initialValue !== 0) {
      this.data.fill(initialValue);
    }
  }

  /**
   * Create a grid from bounding box
   */
  static fromBounds(bounds: Box3, config: VoxelGridConfig): VoxelGrid {
    const { cellSize, padding } = config;
    
    const min = bounds.min.clone().subScalar(padding);
    const max = bounds.max.clone().addScalar(padding);
    
    const counts = {
      x: Math.ceil((max.x - min.x) / cellSize) + 1,
      y: Math.ceil((max.y - min.y) / cellSize) + 1,
      z: Math.ceil((max.z - min.z) / cellSize) + 1,
    };

    const origin = min.clone();
    const gridBounds = new Box3(
      origin.clone(),
      new Vector3(
        origin.x + (counts.x - 1) * cellSize,
        origin.y + (counts.y - 1) * cellSize,
        origin.z + (counts.z - 1) * cellSize,
      ),
    );

    const info: VoxelGridInfo = {
      counts,
      origin,
      cellSize,
      bounds: gridBounds,
      total: counts.x * counts.y * counts.z,
      strides: {
        x: counts.y * counts.z,
        y: counts.z,
        z: 1,
      },
    };

    return new VoxelGrid(info);
  }

  /**
   * Get flat array index from 3D coordinates
   */
  index(x: number, y: number, z: number): number {
    const { strides } = this.info;
    return x * strides.x + y * strides.y + z * strides.z;
  }

  /**
   * Get 3D coordinates from flat array index
   */
  coords(idx: number): { x: number; y: number; z: number } {
    const { strides, counts } = this.info;
    const x = Math.floor(idx / strides.x);
    const rem = idx % strides.x;
    const y = Math.floor(rem / strides.y);
    const z = rem % strides.y;
    return { x, y, z };
  }

  /**
   * Get world-space position from grid coordinates
   */
  worldPosition(x: number, y: number, z: number): Vector3 {
    const { origin, cellSize } = this.info;
    return new Vector3(
      origin.x + x * cellSize,
      origin.y + y * cellSize,
      origin.z + z * cellSize,
    );
  }

  /**
   * Get grid coordinates from world position
   */
  gridCoords(pos: Vector3): { x: number; y: number; z: number } {
    const { origin, cellSize } = this.info;
    return {
      x: Math.floor((pos.x - origin.x) / cellSize),
      y: Math.floor((pos.y - origin.y) / cellSize),
      z: Math.floor((pos.z - origin.z) / cellSize),
    };
  }

  /**
   * Check if coordinates are within bounds
   */
  inBounds(x: number, y: number, z: number): boolean {
    const { counts } = this.info;
    return x >= 0 && x < counts.x &&
           y >= 0 && y < counts.y &&
           z >= 0 && z < counts.z;
  }

  /**
   * Get value at coordinates (returns NaN if out of bounds)
   */
  get(x: number, y: number, z: number): number {
    if (!this.inBounds(x, y, z)) return NaN;
    return this.data[this.index(x, y, z)];
  }

  /**
   * Set value at coordinates
   */
  set(x: number, y: number, z: number, value: number): void {
    if (!this.inBounds(x, y, z)) return;
    this.data[this.index(x, y, z)] = value;
  }

  /**
   * Clone this grid
   */
  clone(): VoxelGrid {
    const grid = new VoxelGrid(this.info);
    grid.data.set(this.data);
    return grid;
  }

  /**
   * Fill all values
   */
  fill(value: number): void {
    this.data.fill(value);
  }

  /**
   * Count voxels matching a predicate
   */
  count(predicate: (value: number) => boolean): number {
    let count = 0;
    for (const v of this.data) {
      if (predicate(v)) count++;
    }
    return count;
  }

  /**
   * Iterate over all voxels with coordinates
   */
  forEach(callback: (x: number, y: number, z: number, value: number, idx: number) => void): void {
    const { counts } = this.info;
    let idx = 0;
    for (let x = 0; x < counts.x; x++) {
      for (let y = 0; y < counts.y; y++) {
        for (let z = 0; z < counts.z; z++) {
          callback(x, y, z, this.data[idx], idx);
          idx++;
        }
      }
    }
  }

  /**
   * Map values to a new grid
   */
  map(fn: (value: number, x: number, y: number, z: number) => number): VoxelGrid {
    const result = new VoxelGrid(this.info);
    const { counts } = this.info;
    let idx = 0;
    for (let x = 0; x < counts.x; x++) {
      for (let y = 0; y < counts.y; y++) {
        for (let z = 0; z < counts.z; z++) {
          result.data[idx] = fn(this.data[idx], x, y, z);
          idx++;
        }
      }
    }
    return result;
  }
}

